// features/auth/authSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

interface AuthState {
    user: any | null;
    token: string | null;
    isAuthenticated: boolean;
    userData: Object;
    loading: boolean;
    initialized: boolean;
    error: { message: string, status: boolean } | null;
}

const initialState: AuthState = {
    user: null,
    token: null,
    userData: {},
    loading: false,
    isAuthenticated: false,
    initialized: false,
    error: null,
};

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ emailOrUname, password }: { emailOrUname: string; password: string }, thunkAPI) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/login`, { emailOrUname, password });
            return response.data;
        } catch (err: any) {
            return thunkAPI.rejectWithValue(err.response?.data || { message: 'Server error', status: false });
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.initialized = true;
            localStorage.removeItem('viart_token');
            localStorage.removeItem('viart_user');
        },
        restoreAuth(state) {
            const token = localStorage.getItem("viart_token");
            const user = localStorage.getItem("viart_user");

            if (token && user) {
                // Verify token is not expired before restoring
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const isExpired = payload.exp && (payload.exp * 1000) < Date.now();
                    if (isExpired) {
                        // Token expired — clean up
                        localStorage.removeItem('viart_token');
                        localStorage.removeItem('viart_user');
                        state.isAuthenticated = false;
                    } else {
                        state.token = token;
                        state.user = JSON.parse(user);
                        state.isAuthenticated = true;
                    }
                } catch {
                    // Malformed token — clean up
                    localStorage.removeItem('viart_token');
                    localStorage.removeItem('viart_user');
                    state.isAuthenticated = false;
                }
            } else {
                state.isAuthenticated = false;
            }
            state.initialized = true;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(loginUser.pending, state => {
                state.loading = true;
                state.error = null;
                state.isAuthenticated = false;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.token = action.payload.token;
                state.user = action.payload.user;
                // state.userData = action.payload.userData;
                state.isAuthenticated = true;
                localStorage.setItem('viart_token', action.payload.token);
                localStorage.setItem('viart_user', JSON.stringify(action.payload.user));
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as { message: string, status: boolean };
                state.isAuthenticated = false;
            });
    },
});

export const { logout, restoreAuth } = authSlice.actions;
export default authSlice.reducer;
